import { apiClient } from "@lobby/api-client";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { emitter, useAuth } from "../../shared";
import type { PropsWithChildren } from "react";
import {
  type ISlot,
  type ISlotView,
  type IWin,
  JackpotContext,
  JackpotMap,
  type TJackpotsType,
  useRpcWebsocket,
} from "../../entities/jackpot";

export const JackpotContextProvider = ({ children }: PropsWithChildren) => {
  const [slots, setSlots] = useState<ISlot[] | null>(null);
  const [wins, setWins] = useState<IWin[] | null>(null);
  const [isOpened, setIsOpened] = useState(false);
  const {
    addWinsListener,
    acceptWin,
    handleGetSlotsAndWins,
    connect,
    addSlotsListener,
    isOpen,
    init,
    error,
  } = useRpcWebsocket();

  const { isAuth } = useAuth();

  const { data: lobbyData } = useQuery({
    enabled: isAuth,
    queryKey: ["Lobby.getCurrent"],
    queryFn: () =>
      apiClient.send({
        endpoint: "Lobby.getCurrent",
      }),
  });

  const isJackpotsActive = Boolean(lobbyData?.result?.jackpots?.isActive);

  const JackpotsStreamQuery = useQuery({
    enabled: isAuth && isJackpotsActive,
    queryKey: ["Lobby.getWsStream"],
    queryFn: () => apiClient.send({ endpoint: "Lobby.getWsStream" }),
  });

  const streamId = JackpotsStreamQuery?.data?.result ?? null;

  const fetchConnection = useCallback(async () => {
    if (!isOpen) {
      await connect();
    }
  }, [isOpen]);

  const fetchSlotsState = useCallback(async () => {
    if (!isOpen) {
      return;
    }

    const { slots, wins } = await handleGetSlotsAndWins();

    if (slots) {
      setSlots(slots);
    }

    if (wins) {
      setWins(wins);
    }
  }, [isOpen, streamId]);

  const fetchWinAccept = useCallback(
    async (winId: number) => {
      if (!isOpen) {
        return;
      }

      await acceptWin(winId);
      const { slots, wins } = await handleGetSlotsAndWins();

      setWins(wins);
      setSlots(slots);
    },

    [isOpen]
  );

  const addSlotsHandlers = useCallback(() => {
    if (!isOpen) {
      return;
    }

    const slotsListener = (updatedSlots: ISlot[] | null) => {
      if (updatedSlots) {
        setSlots((prevSlots) => {
          const updatedSlotIds = updatedSlots.map(
            (updatedSlot) => updatedSlot.slot
          );
          const newSlots = prevSlots?.map((prevSlot) => {
            const index = updatedSlotIds.indexOf(prevSlot.slot);
            return index !== -1 ? updatedSlots[index] : prevSlot;
          });

          return newSlots ?? prevSlots;
        });
      }
    };

    addSlotsListener(slotsListener);
  }, [isOpen]);

  const addWinHandlers = useCallback(() => {
    if (!isOpen) {
      return;
    }

    addWinsListener((win: IWin | null) => {
      if (win) {
        setWins((prevState) => {
          const filteredWins = (prevState || []).filter(
            ({ id }) => id !== win.id
          );
          if (win.state !== "pending") {
            return filteredWins;
          }
          return [...filteredWins, win];
        });
      }
    });
  }, [isOpen]);

  const computedSlots: ISlotView[] | null = useMemo(() => {
    if (!isOpen) {
      return null;
    }

    const sortedJp = slots?.sort((a, b) => a.slot - b.slot);

    const sortedSlots = sortedJp?.map((slot) => ({
      value: slot.value,
      name: JackpotMap[slot.slot] as TJackpotsType,
      slot: slot.slot,
    }));

    return sortedSlots ?? null;
  }, [isOpen, slots]);

  // const computedWins = useMemo(() => {
  //   if (!isOpen) {
  //     return null;
  //   }

  //   return wins;
  // }, [isOpen, wins]);

  const computedWins = isOpen ? wins : null;
  const pendingWins =
    computedWins?.filter((win) => win.state === "pending") ?? null;

  useEffect(() => {
    if (streamId) {
      init(streamId).then(() => {
        Promise.all([
          fetchConnection(),
          fetchSlotsState(),
          addSlotsHandlers(),
          addWinHandlers(),
        ]);
      });
    } else if (isAuth && isJackpotsActive) {
      JackpotsStreamQuery.refetch();
    }
  }, [
    isOpened,
    isAuth,
    isJackpotsActive,
    addSlotsHandlers,
    addWinHandlers,
    fetchConnection,
    fetchSlotsState,
    init,
    streamId,
  ]);

  useEffect(() => {
    return emitter.on("PLAYER_LOGIN", () => {
      setIsOpened(true);
    });
  }, []);

  return (
    <JackpotContext.Provider
      value={{
        slots: computedSlots,
        wins: pendingWins,
        fetchWinAccept,
        error,
      }}
    >
      {children}
    </JackpotContext.Provider>
  );
};
